import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, InputAdornment, SxProps, TextField, Theme } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { MARKETPLACE_SEARCH_MAX_LENGTH } from '../../../constants/marketplace';
import { marketplaceSetFiltersAction } from '../../../store/actions/marketplace';
import { selectMarketplaceFilters } from '../../../store/selectors/marketplace';

interface IProps {
  // Focus suspends the sticky toolbar's condense, which would otherwise collapse the field
  // the user is typing into.
  onFocusChange?: (isFocused: boolean) => void;
}

const searchFieldSx: SxProps<Theme> = { '& .MuiOutlinedInput-root': { backgroundColor: 'transparent' } };

export const MarketplaceSearchField: React.FC<IProps> = ({ onFocusChange }) => {
  const dispatch = useDispatch();
  const filters = useSelector(selectMarketplaceFilters);

  const handleFocus = useCallback(() => onFocusChange?.(true), [onFocusChange]);
  const handleBlur = useCallback(() => onFocusChange?.(false), [onFocusChange]);

  const handleQueryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(marketplaceSetFiltersAction({ query: event.target.value }));
    },
    [dispatch],
  );

  const handleClearQuery = useCallback(() => {
    dispatch(marketplaceSetFiltersAction({ query: '' }));
  }, [dispatch]);

  const slotProps = useMemo(
    () => ({
      htmlInput: { maxLength: MARKETPLACE_SEARCH_MAX_LENGTH },
      input: {
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 20 }} />
          </InputAdornment>
        ),
        endAdornment: filters.query ? (
          <InputAdornment position="end">
            <IconButton size="small" aria-label="Clear search" onClick={handleClearQuery}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      },
    }),
    [filters.query, handleClearQuery],
  );

  return (
    <TextField
      value={filters.query}
      onChange={handleQueryChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder="Search the marketplace"
      size="small"
      fullWidth
      sx={searchFieldSx}
      slotProps={slotProps}
    />
  );
};
